import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MembershipSuccessPage() {
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const navigate = useNavigate();
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    let cancelled = false;

    const verifySubscription = async () => {
      await new Promise((r) => setTimeout(r, 2000));
      if (cancelled) return;
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/api/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (data.has_membership) {
            if (!cancelled) {
              setVerified(true);
              toast.success('Membership activated successfully!');
            }
          } else if (!cancelled) {
            toast.info('Subscription is processing. Please check back in a moment.');
          }
        }
      } catch {
        // Silent — the fallback view already prompts the user to check back.
      } finally {
        if (!cancelled) setVerifying(false);
      }
    };

    if (sessionId) {
      verifySubscription();
    } else {
      setVerifying(false);
    }

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-black flex items-center justify-center px-6">
      <div className="max-w-2xl w-full">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-12 text-center">
          {verifying ? (
            <>
              <Loader2 className="h-16 w-16 text-emerald-500 animate-spin mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-4">Activating Your Membership</h1>
              <p className="text-zinc-400 text-lg">Please wait while we confirm your subscription...</p>
            </>
          ) : verified ? (
            <>
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-12 w-12 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">Welcome to LeadXchange!</h1>
              <p className="text-zinc-300 text-lg mb-8">
                Your $99/month membership is now active. You can start purchasing leads right away.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/marketplace')}
                  size="lg"
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold shadow-xl"
                  data-testid="browse-leads-btn"
                >
                  Browse Leads
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                <Button
                  onClick={() => navigate('/buyer/dashboard')}
                  size="lg"
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="h-20 w-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-12 w-12 text-amber-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Subscription Processing</h1>
              <p className="text-zinc-400 text-lg mb-8">
                Your payment is being processed. This usually takes a few moments. Check your subscription status on the membership page.
              </p>
              
              <Button
                onClick={() => navigate('/membership')}
                size="lg"
                className="bg-white text-black hover:bg-zinc-200 font-semibold"
                data-testid="check-status-btn"
              >
                Check Status
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
